import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { EditAutomationFunctionsComponent } from 'src/app/components/edit-automation-functions/edit-automation-functions.component';
import { SlackMessageComponent } from 'src/app/components/slack-message/slack-message.component';
import { SlackTextEditorComponent } from 'src/app/components/slack-text-editor/slack-text-editor.component';
import {
  AUTOMATION_ACTION_TYPES_ICONS,
  AUTOMATION_ACTION_TYPES_LABELS,
  STATUS_COLUMN_FORMAT_MAP,
  SUPPORT_STEP_ASSIGNEE_TYPES,
} from 'src/app/constants';
import { extractTicketId } from 'src/app/utils/custom-fields';
import { AUTOMATION_ACTION_TYPES, CUSTOM_FIELD_TYPES, ListResponse, TicketTypeField } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { AutomationsService } from 'src/services/automations.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

const STANDARD_RESOLVABLE_USER_FIELDS = [
  {
    id: 'conversation.submitterUserId',
    title: 'Submitter',
    object: {},
  },
  {
    id: 'conversation.assignedToUserId',
    title: 'Assignee',
    object: {},
  },
  {
    id: '',
    title: 'Specific User',
    object: {},
  },
];

@Component({
  selector: 'app-automation-action-step',
  templateUrl: './automation-action-step.component.html',
  styleUrls: ['./automation-action-step.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, SlackTextEditorComponent, SlackMessageComponent],
})
export class AutomationActionStepComponent implements OnInit {
  @Input() index: number;
  @Input() step: any;
  @Input() lookups: Record<string, Record<string, string>> = {};
  @Input() functionNames: string[] = [];
  @Input() functionsVersions: any[] = [];
  @Input() canDrag = false;
  @Output() remove = new EventEmitter<void>();
  @Output() actionDidChange = new EventEmitter<void>();

  public AUTOMATION_ACTION_TYPES = AUTOMATION_ACTION_TYPES;
  public AUTOMATION_ACTION_TYPES_ICONS = AUTOMATION_ACTION_TYPES_ICONS;
  public AUTOMATION_ACTION_TYPES_LABELS = AUTOMATION_ACTION_TYPES_LABELS;

  constructor(
    private searchablePopoverService: SearchablePopoverService,
    private modalCtrl: ModalController,
    private automationsService: AutomationsService,
    private msgSrvc: MessageService,
    private popoverCtrl: PopoverController,
    private apiService: ApiService,
  ) {}

  ngOnInit() {
    // Lady load template html as needed
    if (this.step?.data?.action?.data?.templateId) {
      this.apiService.get(`/templates/${this.step.data.action.data.templateId}`).subscribe(async (template: any) => {
        setTimeout(() => {
          if (!this.lookups.templateHtml) {
            this.lookups.templateHtml = {};
          }
          this.lookups.templateHtml[this.step.data.action.data.templateId] = template.html;
        }, 100);
      });
    }
  }

  selectAssignee(actionData: any) {
    this.searchablePopoverService.assignees({
      event,
      showCreatedByUserOption: false,
      showPrimaryRepOption: false,
      showNoneOption: false,
      callback: async (id: string, _name: string, object: any) => {
        actionData.entityId = id;
        actionData.entityType =
          object.type !== SUPPORT_STEP_ASSIGNEE_TYPES.USER
            ? SUPPORT_STEP_ASSIGNEE_TYPES.GROUP
            : SUPPORT_STEP_ASSIGNEE_TYPES.USER;

        // Keep track in lookups
        if (!this.lookups.assigneeId) {
          this.lookups.assigneeId = {};
        }
        if (!this.lookups.groupId) {
          this.lookups.groupId = {};
        }

        if (actionData.entityType === SUPPORT_STEP_ASSIGNEE_TYPES.USER) {
          this.lookups.assigneeId[id] = object.name;
        } else {
          this.lookups.groupId[id] = object.name;
        }
      },
    });
  }

  selectSubStatus(actionData: any) {
    this.searchablePopoverService.subStatusKeys({
      event,
      callback: async (id: string, name: string) => {
        actionData.subStatusKey = id;

        if (!this.lookups.subStatusKey) {
          this.lookups.subStatusKey = {};
        }

        this.lookups.subStatusKey[id] = name;
      },
    });
  }

  getSubStatusIcon(subStatusKey: string) {
    const type = subStatusKey.split('__')[0];

    return STATUS_COLUMN_FORMAT_MAP[type]?.icon;
  }

  getSubStatusIconClass(subStatusKey: string) {
    const type = subStatusKey.split('__')[0];

    return STATUS_COLUMN_FORMAT_MAP[type]?.iconClass;
  }

  selectTag(actionData: any) {
    this.searchablePopoverService.tags({
      event,
      callback: async (id: string, _name: string, object: any) => {
        const list = actionData.tagIds || [];
        if (list.includes(id)) {
          return;
        }

        actionData.tagIds = [...list, id];

        if (!this.lookups.tagId) {
          this.lookups.tagId = {};
        }

        this.lookups.tagId[id] = object.name;
      },
    });
  }

  removeTag(actionData: any, index: number) {
    actionData.tagIds.splice(index, 1);
  }

  selectTicketType(actionData: any) {
    this.searchablePopoverService.ticketTypes({
      event,
      callback: async (id: string, _name: string, object: any) => {
        actionData.ticketTypeId = id;

        if (!this.lookups.ticketTypeId) {
          this.lookups.ticketTypeId = {};
        }

        this.lookups.ticketTypeId[id] = object.name;
      },
    });
  }

  selectSlackUserGroup(actionData: any) {
    this.searchablePopoverService.slackUserGroups({
      event,
      callback: async (id: string, _name: string, object: any) => {
        actionData.slackUserGroupId = id;

        if (!this.lookups.slackGroupId) {
          this.lookups.slackGroupId = {};
        }

        this.lookups.slackGroupId[id] = object.name;
      },
    });
  }

  selectUserForList(actionData: any) {
    this.searchablePopoverService.users({
      event,
      showAssignToMeText: '',
      showNoneOption: false,
      callback: async (id: string, _name: string, object: any) => {
        if (actionData.userIds.includes(id)) {
          return;
        }

        actionData.userIds.push(id);

        if (!this.lookups.assigneeId) {
          this.lookups.assigneeId = {};
        }

        this.lookups.assigneeId[id] = object.name;
      },
    });
  }

  selectTemplate(actionData: any) {
    this.searchablePopoverService.templates({
      event,
      showNoneOption: true,
      selectedTemplateId: actionData.templateId,
      callback: (id: string, _title: string, data: any) => {
        actionData.templateId = id;

        if (!this.lookups.templateId) {
          this.lookups.templateId = {};
        }

        if (!this.lookups.templateHtml) {
          this.lookups.templateHtml = {};
        }

        this.lookups.templateId[id] = data.name;
        this.lookups.templateHtml[id] = data.html;
      },
    });
  }

  selectSlackChannel(actionData: any) {
    this.searchablePopoverService.slackChannels({
      event,
      callback: async (id: string, _name: string, object: any) => {
        actionData.channelId = id;

        if (!this.lookups.slackChannelId) {
          this.lookups.slackChannelId = {};
        }

        this.lookups.slackChannelId[id] = object.name;
      },
    });
  }

  removeUserFromList(actionData: any, index: number) {
    actionData.userIds.splice(index, 1);
  }

  // If your action is "triggerFunction" and you had an Edit Functions modal:
  async editFunctions() {
    const customActionsVersion = this.getFunctionsVersion('draft') ?? this.getFunctionsVersion('active');

    const modal = await this.modalCtrl.create({
      component: EditAutomationFunctionsComponent,
      cssClass: 'modal-full-screen',
      componentProps: {
        version: customActionsVersion.version,
        status: customActionsVersion.status,
      },
      keyboardClose: false,
      backdropDismiss: false,
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getFunctionsVersions();
    });
  }

  async getFunctionsVersions() {
    try {
      const functionsVersionsResponse: any = await this.automationsService.getAllFunctionsVersions();

      this.functionsVersions = functionsVersionsResponse;

      this.functionNames = this.getFunctionsVersion('active').exportedFunctionNames;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  getFunctionsVersion(status: string) {
    return (this.functionsVersions ?? []).find((version) => version.status === status);
  }

  handleActionChange() {
    // Could pass the entire action object to parent if needed
    this.actionDidChange.emit();
  }

  async handleOptionsClick() {
    const options: ActionDropdownType[] = [
      {
        label: 'Remove',
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    // Someday:
    // {
    //   label: 'Move up',
    //   icon: 'arrow-up-outline',
    // },
    // {
    //   label: 'Move down',
    //   icon: 'arrow-down-outline',
    // },

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number, label: string) => {
          popover.dismiss();

          this.remove.emit();
        },
      },
      event,
    });
    popover.present();
  }

  toggleWorkingTimeOnly(actionData: any) {
    actionData.wait.workingTimeOnly = !actionData.wait.workingTimeOnly;
  }

  toggleCreateTemporaryTriageChannel(actionData: any) {
    if (!actionData.createChannel) {
      actionData.createChannel = {
        prefix: 'triage-',
        isPrivate: false,
      };
      actionData.channelId = undefined;
    } else {
      actionData.createChannel = undefined;
    }
  }

  compareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  selectResolvableUserForGoogleGroup(actionData: any) {
    const selectedValue = actionData.userId;

    this.searchablePopoverService.showPopover({
      event,
      showImages: false,
      showSearch: true,
      selectedItemId:
        typeof selectedValue === 'object' && 'valueFrom' in selectedValue && selectedValue.valueFrom
          ? selectedValue.valueFrom
          : '',
      callback: (id: string, name: string, object: any) => {
        if (!id) {
          actionData.userId = {
            value: '',
          };
        } else {
          actionData.userId = {
            valueFrom: id,
          };

          if (id.startsWith('conversation.ticketTypeFields[')) {
            const ticketTypeFieldId = extractTicketId(id);

            if (!this.lookups.customFieldId) {
              this.lookups.customFieldId = {};
            }

            this.lookups.customFieldId[ticketTypeFieldId] = name;
          }
        }
      },
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();

        const options = [...STANDARD_RESOLVABLE_USER_FIELDS.filter((f) => f.id !== '')];

        const ticketTypesResponse: ListResponse<TicketTypeField> = await this.apiService.postPromise(
          '/custom-fields/list',
          {
            limit: null,
            order: ['name'],
            descending: false,
            where: [
              {
                field: 'type',
                operator: 'in',
                value: [CUSTOM_FIELD_TYPES.USER_SELECT],
              },
            ],
          },
        );

        ticketTypesResponse.data.map((f) => {
          options.push({
            title: `[${f.ticketType?.name}] ${f.name}`, // Show the ticket type name in the label to disambiguate
            id: `conversation.ticketTypeFields['${f.id}']`,
            object: {},
          });
        });

        options.push(STANDARD_RESOLVABLE_USER_FIELDS.find((f) => f.id === ''));

        return options.filter((f) => !cleanQuery || f.title.toLowerCase().includes(cleanQuery.toLowerCase()));
      },
    });
  }

  selectSpecificUserForGoogleGroup(actionData: any) {
    this.searchablePopoverService.users({
      event,
      showNoneOption: false,
      selectedUserId: actionData.userId?.value,
      callback: (id: string, name: string, object: any) => {
        actionData.userId = {
          value: id,
        };

        if (!this.lookups.assigneeId) {
          this.lookups.assigneeId = {};
        }

        this.lookups.assigneeId[id] = name;
      },
    });
  }

  getResolvableUserLabel(resolvableUser: any) {
    const id =
      typeof resolvableUser === 'object' && 'valueFrom' in resolvableUser && resolvableUser.valueFrom
        ? resolvableUser.valueFrom
        : '';

    if (id.startsWith('conversation.ticketTypeFields[')) {
      const ticketTypeFieldId = extractTicketId(id);

      return this.lookups.customFieldId?.[ticketTypeFieldId];
    }

    return STANDARD_RESOLVABLE_USER_FIELDS.find((f) => f.id === id)?.title;
  }

  showSpecificUserSelection(actionData: any) {
    return actionData.userId && !('valueFrom' in actionData.userId);
  }
}
